import React, { useEffect, useMemo } from "react";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Typography,
  Card,
  CircularProgress,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { ActivityItem } from "../components/ActivityFeed/ActivityItem";
import { useActivityStore, groupPhotoActivities } from "../store/activityStore";
import { useAuthStore } from "../store/authStore";
import { useReactionStore } from "../store/reactionStore";

export const ActivityFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const {
    activities,
    loading,
    error,
    hasMore,
    fetchFeed,
    loadMore,
    clearActivities,
  } = useActivityStore();
  const { fetchReactionTypes } = useReactionStore();
  const groupedActivities = useMemo(() => groupPhotoActivities(activities), [activities]);

  // Load reaction types on mount
  useEffect(() => {
    fetchReactionTypes();
  }, [fetchReactionTypes]);

  useEffect(() => {
    if (profile?.id) {
      fetchFeed(0);
    }

    return () => {
      clearActivities();
    };
  }, [profile?.id, fetchFeed, clearActivities]);

  const handleRefresh = () => {
    fetchFeed(0);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f0e8",
        paddingBottom: 4,
      }}
    >
      <Container maxWidth="md" sx={{ paddingTop: 2 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ marginBottom: 3 }}
        >
          <IconButton
            variant="outlined"
            size="sm"
            onClick={() => navigate(-1)}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <DynamicFeedIcon sx={{ color: "primary.500" }} />
          <Typography level="h4">Activity Feed</Typography>
        </Stack>

        {/* Error State */}
        {error && (
          <Card variant="soft" color="danger" sx={{ marginBottom: 2, padding: 2 }}>
            <Typography level="body-sm">{error}</Typography>
          </Card>
        )}

        {/* Loading State (initial) */}
        {loading && activities.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 4,
            }}
          >
            <CircularProgress size="lg" />
          </Box>
        )}

        {/* Empty State */}
        {!loading && activities.length === 0 && !error && (
          <Card
            variant="outlined"
            sx={{
              padding: 4,
              textAlign: "center",
            }}
          >
            <DynamicFeedIcon
              sx={{
                fontSize: 48,
                color: "neutral.400",
                marginBottom: 2,
              }}
            />
            <Typography level="title-md" sx={{ marginBottom: 1 }}>
              No activities to show
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Follow users to see their activities here!
            </Typography>
          </Card>
        )}

        {/* Activity Feed with Infinite Scroll */}
        {groupedActivities.length > 0 && (
          <InfiniteScroll
            dataLength={activities.length}
            next={loadMore}
            hasMore={hasMore}
            loader={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 2,
                }}
              >
                <CircularProgress size="sm" />
              </Box>
            }
            endMessage={
              <Typography
                level="body-xs"
                sx={{
                  textAlign: "center",
                  color: "text.tertiary",
                  padding: 2,
                }}
              >
                You've seen all recent activities
              </Typography>
            }
            refreshFunction={handleRefresh}
            pullDownToRefresh={false}
          >
            <Stack spacing={2}>
              {groupedActivities.map((activity) => (
                <ActivityItem key={activity.id} activity={activity} />
              ))}
            </Stack>
          </InfiniteScroll>
        )}
      </Container>
    </Box>
  );
};
